import React from "react";
import { Check, Minus, ChevronRight, Star, ExternalLink } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
const MacWindow = ({
  image,
  alt,
  glowColor = "primary"
}: {
  image: string;
  alt: string;
  glowColor?: string;
}) => <div className={`relative rounded-xl border border-border bg-card/50 backdrop-blur-sm overflow-hidden transition-all duration-500 hover:scale-[1.02] shadow-2xl ${glowColor === 'purple' ? 'shadow-secondary/40' : 'shadow-primary/20'}`}>
    {}
    <div className="bg-muted/80 px-4 py-3 flex items-center gap-2 border-b border-border/50">
      <div className="size-3 rounded-full bg-[#FF5F56]" />
      <div className="size-3 rounded-full bg-[#FFBD2E]" />
      <div className="size-3 rounded-full bg-[#27C93F]" />
    </div>
    {}
    <div className="relative aspect-video w-full bg-muted/20">
      {image ? <img src={image} alt={alt} className="h-full w-full object-cover" loading="lazy" /> : <div className="flex h-full items-center justify-center text-muted-foreground">
          No Preview Available
        </div>}
    </div>
  </div>;
const DeepDiveList = () => {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="space-y-24">
        {[1, 2, 3].map(i => <div key={i} className="grid grid-cols-1 gap-12 md:grid-cols-2 items-center" data-index={i}>
            <div className="aspect-video w-full rounded-xl bg-muted/20 animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 w-3/4 bg-muted/20 animate-pulse rounded" />
              <div className="h-4 w-full bg-muted/20 animate-pulse rounded" />
              <div className="h-4 w-5/6 bg-muted/20 animate-pulse rounded" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20">
        <p className="text-destructive mb-4">Failed to load reviews.</p>
        <Button onClick={refetch} variant="outline">Try Again</Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-20 text-muted-foreground">
        No reviews found at the moment.
      </div>;
  }
  return <div className="space-y-24 md:space-y-32">
      {posts.map((post, index) => {
      const isEven = index % 2 === 0;
      const pros = post.customFields?.pros || ["Advanced AI content generation", "Intuitive user interface", "Excellent integration options"];
      const cons = post.customFields?.cons || ["Higher price point for premium features", "Learning curve for advanced tools"];
      const glowColor = index === 0 ? "purple" : "primary";
      return <div key={post.id} data-index={index} className={`grid grid-cols-1 gap-12 lg:gap-20 items-center ${isEven ? 'md:grid-cols-2' : 'md:grid-cols-2 md:[&>*:first-child]:order-2'}`}>
            {}
            <div className="relative group">
              {}
              <div className={`absolute -inset-4 rounded-full blur-3xl opacity-20 -z-10 ${glowColor === 'purple' ? 'bg-secondary' : 'bg-primary'}`} />
              
              <MacWindow image={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559769/img/generic_dashboard_preview.png"} alt={`${post.title} Interface`} glowColor={glowColor} />
            </div>

            {}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary ring-1 ring-inset ring-primary/20">
                    Review #{index + 1}
                  </span>
                  <div className="flex text-yellow-500">
                    {[...Array(5)].map((_, i) => <Star key={i} className="size-4 fill-current" />)}
                  </div>
                </div>
                
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  {post.title}
                </h2>
                
                <div className="text-muted-foreground text-lg leading-relaxed" dangerouslySetInnerHTML={{
              __html: post.excerpt || ""
            }} />
              </div>

              {}
              <Card className="bg-card/50 border-border/50 backdrop-blur-sm p-6 sm:p-8">
                <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
                  <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Pro Verdict
                  </span>
                </h3>
                
                <div className="grid gap-6 sm:grid-cols-2">
                  {}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Pros</h4>
                    <ul className="space-y-2">
                      {Array.isArray(pros) && pros.map((pro: string, i: number) => <li key={i} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 size-4 shrink-0 text-chart-3" />
                          <span>{pro}</span>
                        </li>)}
                    </ul>
                  </div>

                  {}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Cons</h4>
                    <ul className="space-y-2">
                      {Array.isArray(cons) && cons.map((con: string, i: number) => <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Minus className="mt-0.5 size-4 shrink-0 opacity-50" />
                          <span>{con}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </Card>

              {}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                <Link to={post.link || "#"} className="group inline-flex items-center text-primary font-medium hover:text-primary/80 transition-colors">
                  Read Full Expert Review
                  <ChevronRight className="ml-1 size-4 transition-transform group-hover:translate-x-1" />
                </Link>
                
                {}
                <Link to={post.customFields?.affiliate_link || "#"} newTab>
                  <Button variant="default" size="lg" className="rounded-full px-8 shadow-lg shadow-primary/20">
                    Try for Free <ExternalLink className="ml-2 size-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>;
    })}
    </div>;
};
export default function DeepDiveAnalysis() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "DESC"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="deep-dive-analysis" className="dark bg-background text-foreground py-24 sm:py-32 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h2 className="text-base font-semibold leading-7 text-secondary mb-2">
            In-Depth Analysis
          </h2>
          <p className="text-4xl font-bold tracking-tight sm:text-5xl mb-6">
            Top AI Tools Reviewed
          </p>
          <p className="text-lg text-muted-foreground">
            We've tested hundreds of tools so you don't have to. Here is our deep dive into the market leaders for 2026.
          </p>
        </div>

        <PostsProvider wp_query={wp_query}>
          <DeepDiveList />
        </PostsProvider>
      </div>
    </section>;
}